import { withNamespaces } from "react-i18next";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import Board from '@asseinfo/react-kanban';
import TaskCardItem from "./components/TaskCardItem";
import { useQuery } from "@tanstack/react-query";
import KanbanBoardService from "src/modules/data-protection/apis/KanbanBoardService";
import { toast } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import ColumnHeader from "./components/ColumnHeader";
import PageHeader from "src/modules/data-protection/components/page-header";
import './kanban-style.scss';
import FilteringComponent from "./components/FilteringComponent";

const KanbanBoard = ({
    t
}) => {
    const [columns, setColumns] = useState(null);
    const [boardDataIsLoading, setBoardDataIsLoading] = useState(true);
    const [filters, setFilters] = useState({});

    const handleFetchTasksListQuery = useQuery({
        queryKey: [
            "fetch-kanboard-tasks-list-query",
            filters
        ],
        queryFn: async () => {
            setBoardDataIsLoading(true);

            const service = KanbanBoardService.getInstance();

            return await service.fetchList(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching tasks list."), {
                type: "error",
            });
        }
    });

    useEffect(() => {
        const { data } = handleFetchTasksListQuery;

        if (data && Array.isArray(data)) {
            const resultColumns = [];

            data.forEach((item, index) => {
                const itemTasks = item?.data || [];
                const obj = {
                    id: index,
                    title: item.title,
                    cards: itemTasks.map((task) => {
                        return {
                            id: task.id,
                            content: {
                                id: task.id,
                                secondaryId: task.secondary_id,
                                title: task.name,
                                status: task.status,
                                endDate: task.ended_at,
                                priority: task.priority,
                                progressValue: 100,
                                caseId: task.report_case.secondary_case_id,
                                primaryCaseId: task.report_case.case_id,
                                caseTitle: task.report_case.description,
                                isRecurring: task.is_recurring,
                                recurrenceSecondaryId: task.recurrence_secondary_id,
                                team: [
                                    {
                                        id: `task-owner-${task.report_case.secondary_case_id}-${task.ended_at}`,
                                        type: 'Task Owner',
                                        name: task.owner_info?.name,
                                        avatar: task.owner_info?.avatar,
                                    },
                                    {
                                        id: `task-manager-${task.report_case.secondary_case_id}-${task.ended_at}`,
                                        type: 'Task Manager',
                                        name: task.manager_info?.name,
                                        avatar: task.manager_info?.avatar,
                                    },
                                ],
                                questionnaire: task?.questionnaire, // Add questionnaire field
                                caseSecondaryId: task?.case_secondary_id, // Add case secondary ID field
                                reportCase: { // Add report case field
                                    caseId: task?.report_case.case_id,
                                    secondaryCaseId: task?.report_case.secondary_case_id,
                                    isUnreal: task?.report_case.is_unreal,
                                    reportType: task?.report_case.report_type,
                                    software: task?.report_case?.software,
                                    otherAsset: task?.report_case?.other_asset,
                                    control: task?.report_case?.control,
                                    tom: task?.report_case?.tom,
                                },
                                is_mitigation: task?.is_mitigation,
                                task_secondary_id: task?.secondary_id,
                                asset_id: task?.asset_id,
                                risk_secondary_id: task?.risk_secondary_id,
                            }
                        }
                    }),
                    columnsubtitle: `${itemTasks.length}`,
                };

                resultColumns.push(obj);
            });

            setColumns(resultColumns);
        }

    }, [handleFetchTasksListQuery.dataUpdatedAt]);

    const renderLoadingSection = () => {
        return (
            <Row>
                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '50%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '500px' }}></div>
                </Col>
                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '50%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '500px' }}></div>
                </Col>
                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '50%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '500px' }}></div>
                </Col>
                <Col sm="12" md="3">
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '50%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '500px' }}></div>
                </Col>
            </Row>
        )
    }

    useEffect(() => {
        if (columns?.length > 0) {
            setBoardDataIsLoading(false);
        }
    }, [columns]);

    const handleFiltersChange = useCallback((newFilters) => {
        setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
    }, []);

    const renderBoard = () => {
        return (
            <div className='d-flex'>
                <Board initialBoard={{
                    sortBy: filters.sortBy,
                    columns: columns,
                    placeholder: null
                }}
                    allowAddCard={false}
                    disableColumnDrag={true}
                    disableCardDrag={true}
                    renderColumnHeader={({ title, columnsubtitle }) => {
                        return (
                            <ColumnHeader title={title} subtitle={columnsubtitle} />
                        );
                    }}
                    renderCard={({ content }, { dragging }) => {
                        return (
                            <TaskCardItem data={content} dragging={false} />
                        );
                    }}
                    onNewCardConfirm={(draftCard) => ({
                        id: new Date().getTime(),
                        ...draftCard,
                    })}
                />
            </div>
        );
    }

    return (
        <div className='page-content supplier-kanban-board-page'>
            <Container fluid>
                <PageHeader title={t('Kanban Board')} />

                <Row className="mb-4">
                    <Col sm="12">
                        <div className="d-flex gap-2 justify-content-end">
                            <FilteringComponent
                                onChange={handleFiltersChange}
                            />
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col sm="12">
                        {
                            boardDataIsLoading ? (
                                <>{renderLoadingSection()}</>
                            ) : (
                                <>{renderBoard()}</>
                            )
                        }
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { Organization } = state;

    return {
        user: state.Login.user,
        organization: Organization
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(KanbanBoard))
);